import { useEffect, useRef } from 'react';
import { useKnowledgeStore } from '@/stores/knowledge';
import Graph from 'graphology';
import { circular } from 'graphology-layout';
import forceAtlas2 from 'graphology-layout-forceatlas2';

const ENTITY_COLORS: Record<string, string> = {
  market_concept: '#3b82f6',
  buyer_scenario: '#8b5cf6',
  purchase_driver: '#f59e0b',
  product_attribute: '#10b981',
  consumer_need: '#ef4444',
  brand_perception: '#ec4899',
};

// Semantic relation groups for edge coloring
const RELATION_GROUPS: Record<string, { color: string; label: string }> = {
  // Concept ↔ Concept — hierarchical
  subset_of:        { color: '#60a5fa', label: 'Hierarchical' },
  broader_than:     { color: '#60a5fa', label: 'Hierarchical' },
  // Concept ↔ Concept — lateral
  complements:      { color: '#34d399', label: 'Complementary' },
  co_activates_with:{ color: '#34d399', label: 'Complementary' },
  // Concept ↔ Concept — competitive
  competes_with:    { color: '#f87171', label: 'Competitive' },
  contrasts_with:   { color: '#f87171', label: 'Competitive' },
  // Concept ↔ Concept — causal
  amplifies:        { color: '#fb923c', label: 'Amplifying' },
  enables:          { color: '#fb923c', label: 'Amplifying' },
  precondition_for: { color: '#fb923c', label: 'Amplifying' },
  // Scenario ↔ Scenario — journey
  precedes_in_journey:{ color: '#a78bfa', label: 'Journey' },
  escalates_to:     { color: '#a78bfa', label: 'Journey' },
  triggers:         { color: '#a78bfa', label: 'Journey' },
  // Scenario ↔ Scenario — substitution
  substitutes_for:  { color: '#f472b6', label: 'Substitution' },
  variant_of:       { color: '#f472b6', label: 'Substitution' },
  concurrent_with:  { color: '#f472b6', label: 'Substitution' },
  // Cross-type
  activates:             { color: '#facc15', label: 'Cross-type' },
  seeks_fulfillment_from:{ color: '#facc15', label: 'Cross-type' },
  signals_intent_for:    { color: '#facc15', label: 'Cross-type' },
  creates_barrier_against:{ color: '#f97316', label: 'Barrier' },
  drives_consideration_in:{ color: '#facc15', label: 'Cross-type' },
  inhibits_consideration_in:{ color: '#f97316', label: 'Barrier' },
};

function getEdgeColor(relType: string): string {
  return RELATION_GROUPS[relType]?.color ?? 'rgba(107,114,128,0.4)';
}

function formatRelType(relType: string): string {
  return relType.replace(/_/g, ' ');
}

export function CanonicalGraph() {
  const { subgraph, graphLoading, selectedCanonical } = useKnowledgeStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!subgraph || !canvasRef.current) return;
    if (subgraph.nodes.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.parentElement?.getBoundingClientRect();
    canvas.width = rect?.width || 600;
    canvas.height = rect?.height || 400;

    const graph = new Graph();

    subgraph.nodes.forEach((node) => {
      graph.addNode(node.id, {
        label: node.name,
        size: node.id === selectedCanonical?.id ? 12 : 8,
        color: ENTITY_COLORS[node.entity_type] || '#6b7280',
        x: 0,
        y: 0,
      });
    });

    subgraph.edges.forEach((edge) => {
      if (graph.hasNode(edge.source) && graph.hasNode(edge.target)) {
        try {
          graph.addEdge(edge.source, edge.target, {
            label: edge.relation_type,
            relType: edge.relation_type,
            weight: edge.weight,
          });
        } catch {
          // Skip duplicate edges
        }
      }
    });

    circular.assign(graph);
    if (graph.order > 1) {
      forceAtlas2.assign(graph, { iterations: 50, settings: { gravity: 1 } });
    }

    // Normalize positions to canvas
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    graph.forEachNode((_, attrs) => {
      if (attrs.x < minX) minX = attrs.x;
      if (attrs.x > maxX) maxX = attrs.x;
      if (attrs.y < minY) minY = attrs.y;
      if (attrs.y > maxY) maxY = attrs.y;
    });

    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const pad = 60;

    const scaleX = (v: number) => pad + ((v - minX) / rangeX) * (canvas.width - 2 * pad);
    const scaleY = (v: number) => pad + ((v - minY) / rangeY) * (canvas.height - 2 * pad);

    // Draw
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Edges
    graph.forEachEdge((_, attrs, source, target) => {
      const sA = graph.getNodeAttributes(source);
      const tA = graph.getNodeAttributes(target);
      const sx = scaleX(sA.x);
      const sy = scaleY(sA.y);
      const tx = scaleX(tA.x);
      const ty = scaleY(tA.y);
      const color = getEdgeColor(attrs.relType || '');
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(tx, ty);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.65;
      ctx.stroke();
      ctx.globalAlpha = 1;

      // Relation label at midpoint
      if (attrs.relType) {
        const mx = (sx + tx) / 2;
        const my = (sy + ty) / 2;
        ctx.font = '9px Inter, sans-serif';
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.fillText(formatRelType(attrs.relType).slice(0, 20), mx, my - 3);
      }
    });

    // Nodes
    graph.forEachNode((id, attrs) => {
      const x = scaleX(attrs.x);
      const y = scaleY(attrs.y);
      ctx.beginPath();
      ctx.arc(x, y, attrs.size, 0, Math.PI * 2);
      ctx.fillStyle = attrs.color;
      ctx.fill();
      if (id === selectedCanonical?.id) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Label
      ctx.fillStyle = '#d1d5db';
      ctx.font = '11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(attrs.label?.slice(0, 24) || '', x, y + attrs.size + 14);
    });
  }, [subgraph, selectedCanonical]);

  if (graphLoading) {
    return <div className="flex items-center justify-center h-full text-gray-500">Loading graph...</div>;
  }

  if (!subgraph || subgraph.nodes.length === 0) {
    return <div className="flex items-center justify-center h-full text-gray-500">No graph data available</div>;
  }

  // Build unique relation groups present in this subgraph
  const presentGroups = new Map<string, string>();
  subgraph.edges.forEach((e) => {
    const g = RELATION_GROUPS[e.relation_type];
    if (g && !presentGroups.has(g.label)) {
      presentGroups.set(g.label, g.color);
    }
  });

  return (
    <div className="relative w-full h-full min-h-[300px]">
      <canvas ref={canvasRef} className="w-full h-full" />

      {/* Relation type legend */}
      {presentGroups.size > 0 && (
        <div className="absolute top-2 left-2 bg-gray-900/80 rounded-lg px-2 py-1.5 space-y-1">
          {Array.from(presentGroups.entries()).map(([label, color]) => (
            <div key={label} className="flex items-center gap-1.5 text-xs">
              <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: color }} />
              <span className="text-gray-300">{label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="absolute bottom-2 right-2 text-xs text-gray-500">
        {subgraph.nodes.length} nodes · {subgraph.edges.length} edges
      </div>
    </div>
  );
}
